<template>
  <div>
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="rgba(255, 255, 255, 0.9)"
      color="#1A237E"
      spinner="mini-spinner"
    />

    <ServerError v-if="ServerError" />

    <v-row class="mb-4 align-center px-4 pt-4">
      <v-col cols="12" class="d-flex align-center">
        <v-btn fab small depressed color="blue lighten-5" @click="$router.go(-1)" class="mr-3">
          <v-icon color="#1A237E">mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="page-title">Sighting Analysis</h2>
        <v-spacer></v-spacer>
        <v-chip color="#1A237E" dark label class="font-weight-bold elevation-1">
          <v-icon left small>mdi-database</v-icon>
          ID: {{ $route.query.id.substring(0, 8) }}
        </v-chip>
      </v-col>
    </v-row>

    <v-row class="px-4">
      <v-col cols="12" md="4">
        <v-card flat class="rounded-xl pa-5 mb-5 border-light elevation-1 hover-shadow">
          <div class="text-overline grey--text font-weight-bold">Registered Reporter</div>
          <div class="d-flex align-center mt-3">
            <v-avatar color="#1A237E" size="56" class="mr-4 elevation-2">
              <v-icon dark>mdi-account</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black grey--text text--darken-4">
                {{ user.first_name }} {{ user.last_name }}
              </div>
              <div class="caption blue--text text--darken-2 font-weight-bold">
                <v-icon x-small color="blue">mdi-phone</v-icon> {{ user.phone }}
              </div>
            </div>
          </div>
        </v-card>

        <v-card flat class="rounded-xl pa-5 border-light elevation-1">
          <div class="text-overline grey--text mb-4">Sighting Specifications</div>
          <div class="detail-grid">
            <div class="detail-row" v-for="(item, i) in detailItems" :key="i">
              <v-icon small color="primary" class="mr-3">{{ item.icon }}</v-icon>
              <div class="detail-content">
                <span class="detail-label">{{ item.label }}</span>
                <span class="detail-value">{{ item.value || 'Not Specified' }}</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card flat class="rounded-xl overflow-hidden elevation-3 mb-6 border-white-thick">
          <div class="map-overlay-label">
            <v-icon small color="white" class="mr-1">mdi-map-marker-check</v-icon> Verified Location
          </div>
          <div id="googlemap2"></div>
        </v-card>

        <v-tabs v-model="activeTab" color="#1A237E" slider-color="#FF7043" class="rounded-t-xl elevation-1">
          <v-tab><v-icon left small>mdi-camera-outline</v-icon> Rescue Photos</v-tab>
          <v-tab v-if="video && video.length > 0"><v-icon left small>mdi-video-outline</v-icon> Video</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="rounded-b-xl border-light pa-4 white">
          <v-tab-item>
            <v-row v-if="photos.length > 0">
              <v-col v-for="(img, i) in photos" :key="i" cols="12" sm="6" md="4">
                <v-hover v-slot="{ hover }">
                  <v-card 
                    flat 
                    class="rounded-lg overflow-hidden img-card" 
                    :class="{ 'elevation-4': hover }"
                    @click="openLightbox(mediaURL + img)"
                  >
                    <v-img :src="mediaURL + img" height="200" cover class="grey lighten-4">
                      <v-fade-transition>
                        <div v-if="hover" class="d-flex transition-fast-in-fast-out blue-grey darken-4 v-card--reveal text-h2 white--text" style="height: 100%; opacity: 0.7;">
                          <v-icon dark large>mdi-magnify-plus</v-icon>
                        </div>
                      </v-fade-transition>
                    </v-img>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
            <v-alert v-else type="info" text class="rounded-lg">No photos available for this record.</v-alert>
          </v-tab-item>

          <v-tab-item v-if="video && video.length > 0">
            <div class="video-container rounded-lg overflow-hidden elevation-2">
              <iframe :src="mediaURL + video[0]" frameborder="0" allowfullscreen width="100%" height="400"></iframe>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <v-dialog v-model="lightbox" max-width="1000" overlay-color="#1A237E" overlay-opacity="0.9">
      <v-card color="transparent" flat class="text-right">
        <v-btn fab dark small color="red" @click="lightbox = false" class="mb-2 mr-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-btn fab dark small color="primary" :href="selectedImg" download class="mb-2 mr-4">
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <v-img :src="selectedImg" contain max-height="85vh" class="rounded-xl elevation-24">
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="white"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

/* global google */

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      records: {},
      user: {},
      gmap2: null,
      photos: [],
      video: null,
      activeTab: 0,
      
      // Lightbox Data
      lightbox: false,
      selectedImg: '',

      // Custom Whale Icon
      whaleIcon: {
        url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MCIgaGVpZ2h0PSI1MCIgdmlld0JveD0iMCAwIDY0IDY0Ij48cGF0aCBmaWxsPSIjMUEyMzdFIiBkPSJNNjIgMzJjMC00LTMtOC04LTEwLTYtMi0xNy0zLTI0LTMtMTUgMC0yNCA4LTI2IDEzIDAgMCA1IDMgNiA1czEtNiAzLTYgOC0xIDEwIDJjMCAwIDIgMyA0IDNzMi0tMyA0LTMzYzQgMCAxMiAxIDE4IDMgNSAyIDggNiA4IDZzMC01IDAtOXoiLz48Y2lyY2xlIGZpbGw9IndoaXRlIiBjeD0iMTYiIGN5PSIzMCIgcj0iMSIvPjxjaXJjbGUgZmlsbD0id2hpdGUiIGN4PSIyMCIgY3k9IjI4IiByPSIxIi8+PGNpcmNsZSBmaWxsPSJ3aGl0ZSIgY3g9IjI0IiBjeT0iMjkiIHI9IjEiLz48Y2lyY2xlIGZpbGw9IndoaXRlIiBjeD0iMjgiIGN5PSIyNyIgcj0iMSIvPjxjaXJjbGUgZmlsbD0id2hpdGUiIGN4PSIzMiIgY3k9IjI5IiByPSIxIi8+PGNpcmNsZSBmaWxsPSJ3aGl0ZSIgY3g9IjM2IiBjeT0iMjgiIHI9IjEiLz48L3N2Zz4=',
        scaledSize: new google.maps.Size(64, 64),
        anchor: new google.maps.Point(32, 32)
      },
    };
  },
  computed: {
    detailItems() {
      return [
        { label: 'Vessel Name', value: this.records.boatname, icon: 'mdi-ship-wheel' },
        { label: 'Registration No', value: this.records.boatno, icon: 'mdi-tag-outline' },
        { label: 'Vessel Owner', value: this.records.ownerName, icon: 'mdi-account-tie' },
        { label: 'Sighting Status', value: this.records.field, icon: 'mdi-map-marker-radius' },
        { label: 'Biological Gender', value: this.records.gender, icon: 'mdi-gender-male-female' },
        { label: 'Health Condition', value: this.records.health, icon: 'mdi-heart-pulse' },
        { label: 'Estimated Length', value: this.records.length + ' Feet', icon: 'mdi-ruler-square' },
      ];
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    openLightbox(url) {
      this.selectedImg = url;
      this.lightbox = true;
    },
    initMap(lat, lng) {
      const mapOptions = {
        center: { lat: lat, lng: lng },
        zoom: 13,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { "featureType": "water", "stylers": [{ "color": "#1A237E" }, { "lightness": 18 }] },
          { "featureType": "landscape", "stylers": [{ "color": "#f1f3f5" }] }
        ]
      };
      this.gmap2 = new google.maps.Map(document.getElementById("googlemap2"), mapOptions);

      new google.maps.Marker({
        position: { lat: lat, lng: lng },
        map: this.gmap2,
        icon: this.whaleIcon,
        animation: google.maps.Animation.DROP
      });
    },
    getData() {
      this.appLoading = true;
      axios({
        url: "/record/view/each",
        method: "post",
        headers: { token: localStorage.getItem("token") },
        data: { id: this.$route.query.id }
      })
      .then((res) => {
        this.appLoading = false;
        if (res.data.status) {
          const d = res.data.data;
          this.records = d;
          this.user = res.data.user;
          this.photos = d.photos || [];
          this.video = d.rescueVideo || [];

          if (d.location && d.location.length === 2) {
             this.$nextTick(() => this.initMap(d.location[1], d.location[0]));
          }
        }
      })
      .catch((err) => {
        this.appLoading = false;
        this.ServerError = true;
        console.error(err);
      });
    },
  },
};
</script>

<style scoped>
#googlemap2 { height: 400px; width: 100%; border-radius: 20px; }

.page-title {
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  color: #1A237E;
  letter-spacing: 2px;
}

.detail-grid { display: flex; flex-direction: column; gap: 15px; }
.detail-row { display: flex; align-items: center; padding-bottom: 12px; border-bottom: 1px dashed #e0e0e0; }
.detail-row:last-child { border-bottom: none; }
.detail-content { display: flex; flex-direction: column; }
.detail-label { font-size: 0.75rem; color: #90A4AE; text-transform: uppercase; font-weight: 700; }
.detail-value { font-size: 0.95rem; color: #263238; font-weight: 700; }

.img-card { cursor: pointer; transition: all 0.3s ease; border: 2px solid transparent; }
.img-card:hover { border-color: #1A237E; }
.v-card--reveal { align-items: center; bottom: 0; justify-content: center; position: absolute; width: 100%; }

.border-light { border: 1px solid rgba(0,0,0,0.06) !important; }
.border-white-thick { border: 6px solid white !important; }
.hover-shadow:hover { box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important; }

.map-overlay-label {
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(26, 35, 126, 0.85);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: bold;
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.video-container { background: #000; line-height: 0; }
</style>